/**
 * Fallbacks when options API is not loaded. Prefer inventoryOptions from InventoryContext.
 * Categories, unit statuses, commission types come from /inventory/options/ API.
 */
export const PREDEFINED_CATEGORIES_FALLBACK = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'office', label: 'Office' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'plot', label: 'Plot' },
  { value: 'farmhouse', label: 'Farmhouse' },
  { value: 'farm_land', label: 'Farm Land' },
  { value: 'other', label: 'Other' },
];

export const UNIT_STATUS_FILTERS_FALLBACK = [
  { value: 'all', label: 'All' },
  { value: 'available', label: 'Available' },
  { value: 'booked', label: 'Booked' },
  { value: 'sold', label: 'Sold' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'not_available', label: 'Not Available' },
];

/** Statuses that can be set manually in the table. "booked" is only set when a booking is created. */
export const SETTABLE_UNIT_STATUSES = ['available', 'sold', 'blocked', 'not_available'];

export const COMMISSION_TYPE_OPTIONS_FALLBACK = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed', label: 'Fixed Amount' },
];

/** Format flat commission amount: 1000000 → "10L", 50000 → "50K", else plain number */
function formatFlatCommission(value) {
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.]/g, ''));
  if (Number.isNaN(num)) return '—';
  if (num >= 100000) return `${(num / 100000).toFixed(0)}L`;
  if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
  return num.toLocaleString('en-IN');
}

/** Commission priority: unit → configuration → project. Display: percentage → "5%", flat/fixed → "10L". Default type = percentage. */
export function getEffectiveCommissionDisplay(unit, configuration, project) {
  const unitRate = unit?.commission_rate ?? unit?.commissionRate ?? unit?._raw?.commission_rate;
  const unitType = ((unit?.commission_type ?? unit?.commissionType ?? unit?._raw?.commission_type) || 'percentage').toLowerCase();
  if (unitRate != null && unitRate !== '') {
    const isPercentage = unitType !== 'flat' && unitType !== 'fixed';
    const text = isPercentage ? `${Number(unitRate)}%` : formatFlatCommission(unitRate);
    return { text, source: 'unit' };
  }
  const cfgRate = configuration?.commission ?? configuration?.metadata?.commission_rate;
  const cfgType = ((configuration?.commission_type ?? configuration?.metadata?.commission_type) || 'percentage').toLowerCase();
  if (cfgRate != null && cfgRate !== '') {
    const isPercentage = cfgType !== 'flat' && cfgType !== 'fixed';
    const text = isPercentage ? `${Number(cfgRate)}%` : formatFlatCommission(cfgRate);
    return { text, source: 'cfg' };
  }
  const projRate = project?.commission_rate ?? project?.commission;
  const projType = ((project?.commission_type ?? project?.metadata?.commission_type) || 'percentage').toLowerCase();
  if (projRate != null && projRate !== '') {
    const isPercentage = projType !== 'flat' && projType !== 'fixed';
    const text = isPercentage ? `${Number(projRate)}%` : formatFlatCommission(projRate);
    return { text, source: 'proj' };
  }
  return { text: '—', source: null };
}
